import { HttpClient } from '@zeta-core';
import {
  APIResponse,
  IAcceptInviteRequest,
  IAcceptInviteResponse,
  IAcceptRetailInviteRequest,
  IActivateUserPayload,
  IActivateUserResponse,
  ICheckUserOnboardedByEmailRequest,
  ICheckUserOnboardedByEmailResponse,
  ICreateAdvisorUserPayload,
  ICreateAdvisorUserResponse,
  IDeactivateUserPayload,
  IDocumentUploadRequest,
  IDocumentUploadResponse,
  IEmptyRequestResponse,
  IGetFinancialAvisorsListRequest,
  IGetFinancialAvisorsListResponse,
  IRefreshTokenRequest,
  IResendUserInvitationRequest,
  IResetPasswordRequest,
  IResetPasswordResponse,
  IRetailUserInviteRequest,
  IRetailuserInviteResponse,
  IRetrieveUsernameRequest,
  IRetrieveUsernameResponse,
  ISaveAccessConfigRequest,
  ISubmitResetPasswordRequest,
  ISubmitResetPasswordResponse,
  IUpdateAdvisorUserPayload,
  IUpdateUserResetPasswordRequestRequest,
  IUserInfoByEmailRequest,
  IUserInfoByIDRequest,
  IUserInfoByIDResponse,
  IUserInfoByResponse,
  IUserLogin2FASendRequest,
  IUserLogin2FAVerifyRequest,
  IUserLogin2FAVerifyResponse,
  IUserLoginRequest,
  IUserLoginResponse,
  IValidateUploadCsvPayload,
  IValidateUploadCsvResponse,
  IVerificationSendRequest,
  IVerificationSendResponse,
  IVerificationVerifyRequest,
  IVerificationVerifyResponse,
  IViewAccessConfigRequest,
  IViewAccessConfigResponse,
  IViewAdvisorUserDetailsRequest,
  IViewAdvisorUserDetailsResponse,
} from '@zeta-types';
import { AxiosProgressEvent, AxiosRequestConfig } from 'axios';
import { USER_SERVICE } from '../services-path';
import { IGetUserAccessFeaturesResponse } from './types';

export const UserServiceAPIs = () => {
  return {
    userLogin: (payload: IUserLoginRequest): APIResponse<IUserLoginResponse> => HttpClient.post(`${USER_SERVICE}/user/login`, payload),

    userRefreshToken: (payload: IRefreshTokenRequest) => HttpClient.post(`${USER_SERVICE}/user/refresh/accesstoken`, payload),

    userLogin2FAVerificationSend: (payload: IUserLogin2FASendRequest) => HttpClient.post(`${USER_SERVICE}/user/login/2fa/send`, payload),

    userLogin2FAVerificationVerify: (payload: IUserLogin2FAVerifyRequest): APIResponse<IUserLogin2FAVerifyResponse> =>
      HttpClient.post(`${USER_SERVICE}/user/login/2fa/verify`, payload),

    userInfoByEmail: (payload: IUserInfoByEmailRequest): APIResponse<IUserInfoByResponse> => HttpClient.post(`${USER_SERVICE}/user/viewbyemail`, payload),

    userInfoById: (payload: IUserInfoByIDRequest): APIResponse<IUserInfoByIDResponse> => HttpClient.post(`${USER_SERVICE}/user/basic/search`, payload),

    userDetails: (payload: { userId?: string; login?: boolean }): APIResponse<{ responseItems: IUserInfoByResponse[] }> =>
      HttpClient.post(`${USER_SERVICE}/user/basic/search`, payload),

    getFinancialAvisorsList: (payload: IGetFinancialAvisorsListRequest): APIResponse<IGetFinancialAvisorsListResponse> =>
      HttpClient.post(`${USER_SERVICE}/user/basic/search`, payload),

    // sign-up

    // check if client with email already exists in system
    checkUserOnboardedByEmail: (payload: ICheckUserOnboardedByEmailRequest): APIResponse<ICheckUserOnboardedByEmailResponse> =>
      HttpClient.post(`${USER_SERVICE}/user/verify/email`, payload),

    otpEmailPhoneSendApi: (payload: IVerificationSendRequest): APIResponse<IVerificationSendResponse> =>
      HttpClient.post(`${USER_SERVICE}/user/emailmobile/otp/send`, payload, undefined, true),

    otpEmailPhoneVerifyApi: (payload: IVerificationVerifyRequest): APIResponse<IVerificationVerifyResponse> =>
      HttpClient.post(`${USER_SERVICE}/user/emailmobile/otp/verify`, payload, undefined, true),

    resetPasswordApi: (payload: IResetPasswordRequest): APIResponse<IResetPasswordResponse> => HttpClient.post(`${USER_SERVICE}/user/password/reset`, payload),

    submitResetPasswordRequestApi: (payload: ISubmitResetPasswordRequest): APIResponse<ISubmitResetPasswordResponse> =>
      HttpClient.post(`${USER_SERVICE}/user/submit/resetpw`, payload),

    validateAdvisorUsersCsv: (payload: IValidateUploadCsvPayload): APIResponse<IValidateUploadCsvResponse> =>
      HttpClient.upload(`${USER_SERVICE}/user/advisor/batch/create/csv`, payload),

    inviteRetailUser: (payload: IRetailUserInviteRequest): APIResponse<IRetailuserInviteResponse> =>
      HttpClient.post(`${USER_SERVICE}/user/retail/prospective/invite`, payload),

    createAdvisorUser: (payload: ICreateAdvisorUserPayload): APIResponse<ICreateAdvisorUserResponse> =>
      HttpClient.post(`${USER_SERVICE}/user/advisor/create`, payload),

    updateAdvisorUser: (payload: IUpdateAdvisorUserPayload): APIResponse<IEmptyRequestResponse> =>
      HttpClient.post(`${USER_SERVICE}/user/advisor/role/edit`, payload),

    reActivateUser: (payload: IActivateUserPayload): APIResponse<IActivateUserResponse> => HttpClient.post(`${USER_SERVICE}/user/advisor/reactivate`, payload),

    activateUser: (payload: IDeactivateUserPayload): APIResponse<IEmptyRequestResponse> => HttpClient.post(`${USER_SERVICE}/user/advisor/activate`, payload),

    enableUser: (payload: IActivateUserPayload): APIResponse<IEmptyRequestResponse> => HttpClient.post(`${USER_SERVICE}/user/advisor/enable`, payload),

    deactivateUser: (payload: IDeactivateUserPayload): APIResponse<IEmptyRequestResponse> =>
      HttpClient.post(`${USER_SERVICE}/user/advisor/deactivate`, payload),

    uploadUsersFromCSV: (
      payload: IDocumentUploadRequest,
      onUploadProgress: (progressEvent: AxiosProgressEvent) => void
    ): APIResponse<IDocumentUploadResponse> => HttpClient.upload(`${USER_SERVICE}/user/advisor/batch/create/csv`, payload, { onUploadProgress }),

    viewAdvisorUserDetails: (payload: IViewAdvisorUserDetailsRequest): APIResponse<IViewAdvisorUserDetailsResponse> =>
      HttpClient.post(`${USER_SERVICE}/user/invite/session/view`, payload),

    acceptInvite: (payload: IAcceptInviteRequest): APIResponse<IAcceptInviteResponse> => HttpClient.post(`${USER_SERVICE}/user/advisor/invite/accept`, payload),

    acceptRetailInvite: (payload: IAcceptRetailInviteRequest): APIResponse<IAcceptInviteResponse> =>
      HttpClient.post(`${USER_SERVICE}/user/retail/invite/accept`, payload),

    resendUserInvitation: (payload: IResendUserInvitationRequest): APIResponse<IEmptyRequestResponse> =>
      HttpClient.post(`${USER_SERVICE}/user/advisor/invitation/resend`, payload),

    updateUserResetPasswordRequest: (payload: IUpdateUserResetPasswordRequestRequest): APIResponse<IEmptyRequestResponse> =>
      HttpClient.post(`${USER_SERVICE}/user/authorise/resetpwd`, payload),

    getUserAccessFeatures: (
      payload: IEmptyRequestResponse,
      config?: AxiosRequestConfig<IGetUserAccessFeaturesResponse>
    ): APIResponse<IGetUserAccessFeaturesResponse> => HttpClient.post(`${USER_SERVICE}/user/advisor/access/view`, payload, config),

    viewAccessConfig: (payload: IViewAccessConfigRequest): APIResponse<IViewAccessConfigResponse> =>
      HttpClient.post(`${USER_SERVICE}/user/advisor/customized/view`, payload),

    saveAccessConfig: (payload: ISaveAccessConfigRequest): APIResponse<IEmptyRequestResponse> =>
      HttpClient.post(`${USER_SERVICE}/user/advisor/customized/save`, payload),

    retrieveUsername: (payload: IRetrieveUsernameRequest): APIResponse<IRetrieveUsernameResponse> =>
      HttpClient.post(`${USER_SERVICE}/user/retrieve/username`, payload),
  };
};
